/**
 * Mock 的回复内容与"按输入触发特定错误"的开关。
 *
 * 触发词是故意设计成**斜杠命令**的样子（`/额度`、`/限流`、`/上游`、`/撤回`）：
 * 它们既不会在自然对话里被误触发，也让演示者一眼能记住。
 * 风险词则必须是自然语言——它演示的是"用户说了危险的话，系统如何兜住"，
 * 用命令形式就演示不出真实链路了。
 */

/** 触发 42900（额度已用完）。 */
export const MOCK_TRIGGER_QUOTA = '/额度';
/** 触发 42901（请求过于频繁）。 */
export const MOCK_TRIGGER_RATE_LIMIT = '/限流';
/** 触发 50200（上游不可用）。 */
export const MOCK_TRIGGER_UPSTREAM = '/上游';
/**
 * 触发"整条撤回"：回答会**完整**流式下发，然后被判不安全并撤回。
 *
 * 与 `/额度`、`/限流` 不同：那两个是在开始前就拒绝（error 帧），这条路径是
 * "回答已经完整出现在屏幕上，事后被撤回"，用来验证前端收到 `retract` 帧后
 * 会把正文清掉、只留一张安全提示卡。
 */
export const MOCK_TRIGGER_RETRACT = '/撤回';

/**
 * 撤回时替换正文的公开话术。
 *
 * 与真实服务端同口径：只给求助路径，**不给判定依据**（分类、规则编号、
 * 命中位置都属于审计信息，展示出来可能造成二次伤害）。
 */
export const RETRACT_PUBLIC_MESSAGE =
  '这条回答超出了本平台能提供的范围，已经撤回。如果你正处在困难中，请拨打 12356（全国统一心理援助热线，24 小时）；有立即危险请拨打 110 / 120。';

/**
 * 风险词。
 *
 * 用一组真实的自杀意念表达，而不是"测试风险词"这种占位串：
 * 这段代码同时是**安全话术的验收载体**——它触发的回复必须包含 12356 热线，
 * 用占位串就验收不了这件事。
 */
export const RISK_PATTERN = /自杀|轻生|不想活|想死|自残|割腕|跳楼|结束生命|活着没意思/;

/** 命中风险词时的回复：先承接情绪，再给可执行的求助路径。 */
export const RISK_REPLY = [
  '我听到了，你现在一定非常难受。',
  '',
  '**你的感受是真实的，也值得被认真对待。** 请先不要一个人扛着：',
  '',
  '1. 拨打全国心理援助热线 **12356**（24 小时，免费）；',
  '2. 告诉一个你信得过的人，哪怕只说一句"我最近很不好"；',
  '3. 如果此刻有伤害自己的冲动，请立刻去最近的急诊，或者拨打 **120**。',
  '',
  '我会一直在这里，但热线那头是受过训练、能立刻帮你的人。',
].join('\n');

/** 普通回复池。按"用户说了什么"给不同的、有实质内容的回答。 */
const PLAIN_REPLIES: readonly string[] = [
  [
    '谢谢你把这件事说出来。',
    '',
    '先说一个可能有用的观察：**行为问题常常是关系问题的信号**。',
    '在想办法"怎么让他听话"之前，可以先花两天只做一件事——',
    '留意他在什么时刻是放松的，那通常就是你们之间还畅通的那条通道。',
    '',
    '- 把"你怎么又这样"换成"我看到你现在很烦"',
    '- 一次只谈一件事，别把旧账一起翻出来',
    '- 说完之后留 10 秒沉默，沉默往往比追问更能换来真话',
  ].join('\n'),
  [
    '这件事可以从两个层面看。',
    '',
    '**当下**：先处理情绪，再处理问题。人在激动时听不进道理，这不是态度问题，是生理性的。',
    '',
    '**长期**：稳定的作息、可预期的规则、以及"做错了也不会被否定整个人"的确定感，',
    '比任何一次谈话都更有力量。',
    '',
    '> 如果你愿意多说一点具体场景，我可以帮你把话术改得更贴合你的情况。',
  ].join('\n'),
  '我理解你的担心。先做一个区分：**你能控制的部分**和**你不能控制的部分**。把注意力放在前者，通常一两周内就能看到变化；后者反复琢磨只会消耗你。你想先从哪一件开始？',
];

/**
 * 挑一条回复。
 *
 * 用输入长度做索引而不是随机：同一句话在演示时应该得到同样的回答。
 * 随机会让"我刚才看到的那个建议呢"变成无法复现的体验。
 */
export function pickPlainReply(text: string): string {
  const index = text.length % PLAIN_REPLIES.length;
  return PLAIN_REPLIES[index] ?? PLAIN_REPLIES[0] ?? '';
}

/** 上游不可用时的回复（50200 之前会先吐一点内容，用于验证"已收到的内容不被清空"）。 */
export const UPSTREAM_PARTIAL_REPLY = '我先给你一个初步的想法：这更像是**启动困难**，而不是懒。';
